import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  GroupTitleTemplate,
  PreviewDrawerLineStyled,
  RydeProgress,
} from "@ryde/controls";
import { DECIMAL_RADIX } from "@ryde/shared";
import { blueMui, redMui, rydeColors } from "@ryde/theme";

import { DrawerFormWrapperStyled } from "../drawer";
import { GenericUploadProcessErrorList } from "./generic-upload-process-error-list";
import {
  IGenericUploadFailedItem,
  IGenericUploadProcessStatistics,
} from "./generic-upload.models";
import {
  calculateProgressPercentage,
  getProcessedCount,
  resolveProgressColor,
  resolveProgressTitle,
} from "./helpers/generic-upload-process.helpers";

interface IGenericUploadProcessProps {
  statistics: IGenericUploadProcessStatistics;
  failedItems: IGenericUploadFailedItem[];
  isProcessing: boolean;
}

export const GenericUploadProcess: FC<IGenericUploadProcessProps> = ({
  statistics,
  failedItems,
  isProcessing,
}) => {
  const { t } = useTranslation();

  const processedCount = useMemo(() => {
    return getProcessedCount(statistics);
  }, [statistics]);

  const progressValue = useMemo(
    () =>
      calculateProgressPercentage(processedCount, statistics.totalToProcess),
    [processedCount, statistics.totalToProcess],
  );

  const hasErrors = useMemo(() => statistics.failed > 0, [statistics]);

  const progressTitle = useMemo(
    () => resolveProgressTitle(isProcessing, hasErrors, t),
    [isProcessing, hasErrors, t],
  );

  const progressColor = useMemo(
    () => resolveProgressColor(isProcessing, hasErrors),
    [isProcessing, hasErrors],
  );

  return (
    <DrawerFormWrapperStyled>
      <RydeProgress
        value={parseInt(String(progressValue), DECIMAL_RADIX)}
        title={progressTitle}
        color={progressColor}
        isLoading={isProcessing}
      />

      <GroupTitleTemplate name="results" formLabel={t("general.result")} />

      <PreviewDrawerLineStyled>
        <span>{t("genericUpload.totalToProcess")}</span>
        <span>{statistics.totalToProcess}</span>
      </PreviewDrawerLineStyled>

      <PreviewDrawerLineStyled $numberColor={rydeColors.purpleTitle}>
        <span>{t("genericUpload.successfullyCreated")}</span>
        <span>{statistics.successfullyCreated}</span>
      </PreviewDrawerLineStyled>

      <PreviewDrawerLineStyled $numberColor={blueMui._600}>
        <span>{t("genericUpload.successfullyUpdated")}</span>
        <span>{statistics.successfullyUpdated}</span>
      </PreviewDrawerLineStyled>

      {statistics.failed > 0 && (
        <PreviewDrawerLineStyled $numberColor={redMui._600}>
          <span>{t("genericUpload.failed")}</span>
          <span>{statistics.failed}</span>
        </PreviewDrawerLineStyled>
      )}

      {failedItems.length > 0 && (
        <GenericUploadProcessErrorList failedItems={failedItems} />
      )}
    </DrawerFormWrapperStyled>
  );
};
